// Una campaña de avisos.
//
// Tres cosas en una tarjeta: a cuántos les toca ahora, cuándo sale sola y cómo
// salió la última vez. Esa última columna es la que faltaba: un aviso puede
// llevar semanas roto y, si nadie mira el registro del servidor, nadie se
// entera.

import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

import AnilloPorcentaje from './AnilloPorcentaje';
import { levanta, SUAVE } from '../dashboard/motion';
import { numero } from '../dashboard/formato';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'setiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/* "ayer a las 10:03" se entiende mejor que una fecha completa. */
const cuando = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const f = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const hoy = new Date();
  const dias = Math.round((new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()) - new Date(+m[1], +m[2] - 1, +m[3])) / 86400000);
  const hora = `${m[4]}:${m[5]}`;
  if (dias === 0) return `hoy a las ${hora}`;
  if (dias === 1) return `ayer a las ${hora}`;
  if (dias < 7) return `el ${DIAS[f.getDay()]} a las ${hora}`;
  return `el ${+m[3]} de ${MESES[+m[2] - 1]} a las ${hora}`;
};

export default function TarjetaCampana({ campana, onEnviar, enviando = false, color = 'primary' }) {
  const [paso, setPaso] = useState('inicio');   // inicio | confirmar | listo | falloAlArrancar
  const [recienTermino, setRecienTermino] = useState(false);

  /* El envio corre en el servidor; la pantalla solo lo sigue. Cuando deja de
     estar en curso, lo que hay que mostrar ya esta en la propia campaña. */
  useEffect(() => {
    if (enviando) setRecienTermino(true);
    else if (recienTermino) { setPaso('listo'); setRecienTermino(false); }
  }, [enviando, recienTermino]);

  const fallo = campana.ultimoEnvio && campana.ultimoFallados > 0 && campana.ultimoEnviados === 0;
  const parcial = campana.ultimoEnvio && campana.ultimoFallados > 0 && campana.ultimoEnviados > 0;
  const bien = campana.ultimoEnvio && campana.ultimoFallados === 0;

  const mandar = () => {
    setPaso('inicio');
    onEnviar(campana.clave, campana.ultimoEnvio)
      .catch(() => setPaso('falloAlArrancar'));
  };

  return (
    <Card sx={{ p: 3, height: 1, display: 'flex', flexDirection: 'column', ...levanta }}>
      <Stack direction="row" spacing={2.5} alignItems="flex-start">
        <AnilloPorcentaje valor={campana.cuantos} total={campana.universo} color={color} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>
            {campana.nombre}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: .5, mb: 1 }}>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1, color: `${color}.dark` }}>
              {numero(campana.cuantos)}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {campana.audiencia.toLowerCase()}
            </Typography>
          </Stack>

          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {campana.descripcion}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 2.25 }} />

      <Stack spacing={1.25} sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <ScheduleRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: .2 }} />
          <Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>Sale sola</Typography>
            <Typography variant="body2">{campana.automatico}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          {bien && <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'success.main', mt: .2 }} />}
          {(fallo || parcial) && <ErrorRoundedIcon sx={{ fontSize: 18, color: fallo ? 'error.main' : 'warning.main', mt: .2 }} />}
          {!campana.ultimoEnvio && <ScheduleRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: .2 }} />}

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>Último envío</Typography>
            {campana.ultimoEnvio ? (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600, color: fallo ? 'error.dark' : 'text.primary' }}>
                  {numero(campana.ultimoEnviados)} de {numero(campana.ultimoObjetivo)} · {cuando(campana.ultimoEnvio)}
                  {campana.ultimoOrigen === 'M' && ' · a mano'}
                </Typography>
                {campana.ultimoDetalle &&
                  <Typography variant="caption" sx={{ color: 'error.dark', display: 'block', mt: .25 }}>
                    {campana.ultimoDetalle}
                  </Typography>
                }
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Todavía no hay registro
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ mt: 2.25 }}>
        {paso === 'inicio' && !enviando &&
          <Button
            fullWidth variant="contained" color="inherit"
            disabled={campana.cuantos === 0}
            startIcon={<SendRoundedIcon />}
            onClick={() => setPaso('confirmar')}
            sx={{ transition: `transform .3s ${SUAVE}`, '&:hover': { transform: 'translateY(-1px)' } }}
          >
            {campana.cuantos === 0 ? 'No hay a quién avisar' : 'Enviar ahora'}
          </Button>
        }

        {paso === 'confirmar' && !enviando &&
          <Stack spacing={1.25}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Se le manda un WhatsApp a <b>{numero(campana.cuantos)} clientes</b>. A quien ya
              le salió hoy no se le repite.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" onClick={mandar} disabled={enviando}>Sí, enviar</Button>
              <Button size="small" color="inherit" onClick={() => setPaso('inicio')}>Cancelar</Button>
            </Stack>
          </Stack>
        }

        {enviando &&
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: .5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Mandando a {numero(campana.cuantos)}. Toma un par de minutos y sigue
              aunque cierres esta pantalla.
            </Typography>
          </Stack>
        }

        {paso === 'falloAlArrancar' && !enviando &&
          <Stack spacing={1}>
            <Alert severity="error">No pudimos arrancar el envío. Intenta de nuevo en un momento.</Alert>
            <Button size="small" color="inherit" onClick={() => setPaso('inicio')}>Listo</Button>
          </Stack>
        }

        {paso === 'listo' && !enviando &&
          <Stack spacing={1}>
            <Alert severity={campana.ultimoFallados > 0 ? (campana.ultimoEnviados > 0 ? 'warning' : 'error') : 'success'}>
              {campana.ultimoEnviados > 0 && `Salieron ${numero(campana.ultimoEnviados)}.`}
              {campana.ultimoFallados > 0 && ` No se pudo con ${numero(campana.ultimoFallados)}.`}
              {campana.ultimoEnviados === 0 && campana.ultimoFallados === 0 && 'No había a quién avisar.'}
            </Alert>
            <Button size="small" color="inherit" onClick={() => setPaso('inicio')}>Listo</Button>
          </Stack>
        }
      </Box>

      {campana.plantilla &&
        <Chip
          size="small"
          label={campana.plantilla}
          sx={{ mt: 1.5, alignSelf: 'flex-start', bgcolor: 'grey.100', color: 'text.disabled', fontSize: 11 }}
        />
      }
    </Card>
  );
}
